/**
 * Helpers for validating and interpreting a patient's provider assignment history.
 *
 * A patient's `providerAssignments` is an array of
 *   { providerId, effectiveDate, effectiveThruDate|null }
 * where dates are ISO `YYYY-MM-DD` strings and a null/empty `effectiveThruDate`
 * means the assignment is open-ended (ongoing).
 *
 * Business rules enforced here:
 *  - every assignment must have a provider and an effective date
 *  - effectiveThruDate (when present) must be >= effectiveDate
 *  - no two assignments may overlap (a patient has at most one provider on any
 *    single date)
 *  - at most one open-ended (ongoing) assignment may exist
 */

import { normalizeYmdDate } from './dateFormat';

const OPEN_ENDED = '9999-12-31';

function trimStr(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/** Coerce stored date strings (including legacy M/D/YYYY) to YYYY-MM-DD. */
function normalize(value) {
  const raw = trimStr(value);
  if (!raw) return '';
  return normalizeYmdDate(raw) || raw;
}

/** Returns the effective thru date used for range math (open-ended -> sentinel). */
function thruForCompare(assignment) {
  const thru = normalize(assignment.effectiveThruDate);
  return thru || OPEN_ENDED;
}

/**
 * Resolve the "current" assignment for a given day (default today): the one whose
 * range contains the day, preferring open-ended assignments.
 */
export function getCurrentAssignment(assignments, today = new Date().toISOString().slice(0, 10)) {
  const list = Array.isArray(assignments) ? assignments : [];
  const asOf = normalize(today) || trimStr(today);
  return list.find((a) => {
    const eff = normalize(a.effectiveDate);
    const thru = normalize(a.effectiveThruDate);
    if (!eff) return false;
    if (eff > asOf) return false;
    return !thru || thru >= asOf;
  }) || null;
}

/**
 * Classify an assignment relative to a reference day.
 * @returns {'current'|'future'|'historical'}
 */
export function getAssignmentStatus(assignment, today = new Date().toISOString().slice(0, 10)) {
  const eff = normalize(assignment.effectiveDate);
  const thru = normalize(assignment.effectiveThruDate);
  const asOf = normalize(today) || trimStr(today);
  if (eff && eff > asOf) return 'future';
  if (thru && thru < asOf) return 'historical';
  return 'current';
}

/** Returns a new array sorted ascending by effective date. */
export function sortAssignments(assignments) {
  const list = Array.isArray(assignments) ? [...assignments] : [];
  return list.sort((a, b) => normalize(a.effectiveDate).localeCompare(normalize(b.effectiveDate)));
}

/** True when two assignments refer to the same provider + effective + thru range. */
export function sameAssignment(a, b) {
  if (!a || !b) return false;
  return (
    trimStr(a.providerId) === trimStr(b.providerId) &&
    normalize(a.effectiveDate) === normalize(b.effectiveDate) &&
    normalize(a.effectiveThruDate) === normalize(b.effectiveThruDate)
  );
}

/**
 * Clean a provider-assignments array for display or persistence:
 *  - drops entries missing a provider or an effective date (unusable)
 *  - removes exact duplicates (same providerId + effectiveDate + effectiveThruDate)
 *  - coerces dates to YYYY-MM-DD when possible
 *  - returns a new array sorted ascending by effective date
 *
 * This does NOT resolve genuine conflicts (e.g. two different open-ended
 * providers or truly overlapping ranges); those are left intact so
 * `validateProviderAssignments` can surface them.
 */
export function normalizeAssignments(assignments) {
  const list = Array.isArray(assignments) ? assignments : [];
  const seen = new Set();
  const cleaned = [];
  for (const a of list) {
    const providerId = trimStr(a.providerId);
    const eff = normalize(a.effectiveDate);
    if (!providerId || !eff) continue;
    const thru = normalize(a.effectiveThruDate);
    const key = `${providerId}|${eff}|${thru}`;
    if (seen.has(key)) continue;
    seen.add(key);
    cleaned.push({ ...a, providerId, effectiveDate: eff, effectiveThruDate: thru || null });
  }
  return sortAssignments(cleaned);
}

/** UTC-safe "day before" for a YYYY-MM-DD string (avoids local-tz off-by-one). */
function dayBeforeYmd(ymd) {
  const s = normalize(ymd);
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
}

/**
 * Heal a provider-assignments array so it contains no overlaps, for legacy/
 * corrupt data that would otherwise block edits.
 *
 * After de-duping and sorting (open-ended sorts last among equal start dates so
 * an ongoing assignment is preferred), each earlier assignment that overlaps the
 * next one (or is open-ended while a later one exists) has its effectiveThruDate
 * clamped to the day before the next assignment starts. An earlier assignment
 * fully eclipsed by the next start date (e.g. same effectiveDate) is dropped.
 *
 * This intentionally repairs only the supplied (existing) assignments; callers
 * should add any newly entered assignment AFTER repairing, then run
 * validateProviderAssignments so genuine new-input conflicts still surface.
 */
export function repairAssignmentOverlaps(assignments) {
  const deduped = normalizeAssignments(assignments);
  const sorted = [...deduped].sort((a, b) => {
    const ea = normalize(a.effectiveDate);
    const eb = normalize(b.effectiveDate);
    if (ea !== eb) return ea.localeCompare(eb);
    return thruForCompare(a).localeCompare(thruForCompare(b));
  });

  const result = [];
  for (const a of sorted) {
    const cur = { ...a };
    while (result.length > 0) {
      const prev = result[result.length - 1];
      const prevThru = normalize(prev.effectiveThruDate);
      const overlaps = !prevThru || prevThru >= cur.effectiveDate;
      if (!overlaps) break;
      const clamped = dayBeforeYmd(cur.effectiveDate);
      if (!clamped || prev.effectiveDate > clamped) {
        result.pop();
        continue;
      }
      prev.effectiveThruDate = clamped;
      break;
    }
    result.push(cur);
  }
  return result;
}

/**
 * Validate an array of provider assignments.
 * @returns {{ ok: boolean, error?: string }}
 */
export function validateProviderAssignments(assignments) {
  const list = Array.isArray(assignments) ? assignments : [];
  if (list.length === 0) return { ok: true };

  let openEndedCount = 0;
  for (const a of list) {
    const providerId = trimStr(a.providerId);
    const eff = normalize(a.effectiveDate);
    const thru = normalize(a.effectiveThruDate);
    if (!providerId) return { ok: false, error: 'Each provider assignment must have a provider.' };
    if (!eff) return { ok: false, error: 'Each provider assignment must have an effective date.' };
    if (thru && thru < eff) {
      return { ok: false, error: 'Provider effective thru date must be on or after the effective date.' };
    }
    if (!thru) openEndedCount += 1;
  }

  if (openEndedCount > 1) {
    return { ok: false, error: 'Only one provider assignment can be ongoing (no effective thru date).' };
  }

  // Overlap check on sorted ranges.
  const sorted = sortAssignments(list);
  for (let i = 1; i < sorted.length; i += 1) {
    const prevThru = thruForCompare(sorted[i - 1]);
    const currEff = normalize(sorted[i].effectiveDate);
    if (currEff <= prevThru) {
      return {
        ok: false,
        error: 'Provider assignments cannot overlap. A patient can have only one provider on any given date.',
      };
    }
  }

  return { ok: true };
}
